import { useCallback, useEffect, useState } from "react";
import { formatDisplayDate } from "../../services/apiClient";
import { CONSUMPTION_REASON_LABELS } from "../../util/captionPublishingErrors";
import { loadConsumptionAlertsForAdmin } from "../../util/captionConsumptionAlerts";
import "./CaptionConsumptionAlertsPanel.scss";

const CaptionConsumptionAlertsPanel = ({ limit = 20 }) => {
  const [alerts, setAlerts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const loadAlerts = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage("");

    try {
      const items = await loadConsumptionAlertsForAdmin();
      setAlerts(items.slice(0, limit));
    } catch {
      setAlerts([]);
      setErrorMessage("Unable to load paid-plan follow-up alerts.");
    } finally {
      setIsLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    loadAlerts();
  }, [loadAlerts]);

  useEffect(() => {
    const handleAlertsChange = () => loadAlerts();
    window.addEventListener("consumption-alerts-changed", handleAlertsChange);

    return () => {
      window.removeEventListener("consumption-alerts-changed", handleAlertsChange);
    };
  }, [loadAlerts]);

  return (
    <section className="caption-consumption-alerts">
      <div className="caption-consumption-alerts__header">
        <div>
          <h4 className="caption-consumption-alerts__title">Paid plan follow-up</h4>
          <p className="caption-consumption-alerts__subtitle">
            Users flagged after hitting free-plan or resource limits on Caption Publishing.
          </p>
        </div>
        <button type="button" className="caption-consumption-alerts__refresh" onClick={loadAlerts}>
          Refresh
        </button>
      </div>

      {isLoading && <p className="caption-consumption-alerts__message">Loading follow-up list...</p>}
      {errorMessage && <p className="caption-consumption-alerts__hint">{errorMessage}</p>}

      {!isLoading && !alerts.length && (
        <p className="caption-consumption-alerts__message">
          No users flagged for paid plan discussion yet.
        </p>
      )}

      {!isLoading && alerts.length > 0 && (
        <p className="caption-consumption-alerts__hint">
          Showing browser + server alerts. Contact these users to discuss paid plans.
        </p>
      )}

      <div className="caption-consumption-alerts__list">
        {alerts.map((alert) => (
          <article
            key={alert.alertId || `${alert.email}-${alert.flaggedAt}`}
            className="caption-consumption-alerts__item"
          >
            <div className="caption-consumption-alerts__user">
              {alert.pictureUrl && (
                <img
                  src={alert.pictureUrl}
                  alt={alert.name || "User"}
                  className="caption-consumption-alerts__avatar"
                />
              )}
              <div>
                <p className="caption-consumption-alerts__name">{alert.name || "Unknown user"}</p>
                <p className="caption-consumption-alerts__email">{alert.email || "No email"}</p>
              </div>
            </div>

            <div className="caption-consumption-alerts__badges">
              <span className="caption-consumption-alerts__badge">
                {CONSUMPTION_REASON_LABELS[alert.reason] || "Resource limit"}
              </span>
              {alert.hitCount > 1 && (
                <span className="caption-consumption-alerts__badge caption-consumption-alerts__badge--count">
                  {alert.hitCount} hits
                </span>
              )}
            </div>

            <p className="caption-consumption-alerts__meta">
              {formatDisplayDate(alert.flaggedAt)} · {alert.page || "/captionPublishing"}
            </p>
            {alert.message && (
              <p className="caption-consumption-alerts__message-text">{alert.message}</p>
            )}
          </article>
        ))}
      </div>
    </section>
  );
};

export default CaptionConsumptionAlertsPanel;
